/* ============================================================
   BAKKIEBOSS — Toyota Landing Page JavaScript
   DESERT EDITION — Wind-blown dust particles
   Zero dependencies — vanilla JS only
   ============================================================ */

(function () {
  'use strict';

  /* ==========================================================
     1. DESERT DUST FIELD (wind-blown sand particles)
     ========================================================== */
  class DesertDust {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.particles = [];
      this.windSpeed = 1.2;
      this.windGusts = 0;
      this.gustTimer = 0;
      this.raf = null;
      this.paused = false;
      this.isMobile = window.innerWidth <= 768;
      this.resize();
      this.createParticles();
      this.bindEvents();
      this.observeVisibility();
      this.animate();
    }

    resize() {
      const rect = this.canvas.parentElement.getBoundingClientRect();
      this.w = this.canvas.width = rect.width;
      this.h = this.canvas.height = rect.height;
    }

    createParticles() {
      // Fewer particles on mobile for smooth scrolling
      const divisor = this.isMobile ? 20000 : 8000;
      const maxCount = this.isMobile ? 40 : 120;
      const count = Math.min(maxCount, Math.floor((this.w * this.h) / divisor));
      this.particles = [];

      for (let i = 0; i < count; i++) {
        const layer = Math.random(); // 0 = far bg, 1 = close foreground
        this.particles.push({
          x: Math.random() * this.w,
          y: Math.random() * this.h,
          // Larger particles in foreground, tiny in background
          size: layer < 0.3 ? Math.random() * 1 + 0.3
              : layer < 0.7 ? Math.random() * 2 + 0.8
              : Math.random() * 3.5 + 1.5,
          // Wind moves all particles right, faster in foreground
          speedX: (0.3 + layer * 1.5) * (0.8 + Math.random() * 0.4),
          // Slight vertical drift (some rise, some fall)
          speedY: (Math.random() - 0.45) * 0.4,
          opacity: layer < 0.3 ? Math.random() * 0.15 + 0.03
                 : layer < 0.7 ? Math.random() * 0.25 + 0.08
                 : Math.random() * 0.3 + 0.1,
          layer: layer,
          // Sand color variation: warm whites to golden
          hue: Math.floor(Math.random() * 20 + 25),      // 25-45 (golden range)
          sat: Math.floor(Math.random() * 30 + 40),       // 40-70%
          light: Math.floor(Math.random() * 20 + 70),     // 70-90%
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: Math.random() * 0.02 + 0.005,
          wobbleAmp: Math.random() * 0.8 + 0.2,
        });
      }
    }

    bindEvents() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.isMobile = window.innerWidth <= 768;
          this.resize();
          this.createParticles();
        }, 200);
      });
    }

    // Pause animation when hero is off-screen (saves GPU during scroll)
    observeVisibility() {
      const hero = this.canvas.parentElement;
      if (!hero || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          this.paused = !entries[0].isIntersecting;
          if (!this.paused && !this.raf) {
            this.animate();
          }
        },
        { threshold: 0 }
      );
      observer.observe(hero);
    }

    animate() {
      // When hero is off-screen, stop rendering to free GPU for smooth scroll
      if (this.paused) {
        this.raf = null;
        return;
      }

      this.ctx.clearRect(0, 0, this.w, this.h);

      // Wind gusts — periodic surges
      this.gustTimer += 0.005;
      this.windGusts = Math.sin(this.gustTimer) * 0.5
                     + Math.sin(this.gustTimer * 2.3) * 0.3;

      for (const p of this.particles) {
        // Wind effect (stronger for foreground particles)
        const windEffect = (this.windSpeed + this.windGusts) * (0.5 + p.layer * 0.8);
        p.x += p.speedX + windEffect * 0.3;

        // Wobble for natural movement
        p.wobble += p.wobbleSpeed;
        p.y += p.speedY + Math.sin(p.wobble) * p.wobbleAmp * 0.3;

        // Wrap around — particles exit right, enter from left
        if (p.x > this.w + 20) {
          p.x = -20;
          p.y = Math.random() * this.h;
        }
        if (p.y < -10) p.y = this.h + 10;
        if (p.y > this.h + 10) p.y = -10;

        // Draw sand particle
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        this.ctx.fillStyle = `hsla(${p.hue}, ${p.sat}%, ${p.light}%, ${p.opacity})`;
        this.ctx.fill();
      }

      // Draw a few faint horizontal dust streaks (skip on mobile for performance)
      if (this.isMobile) {
        this.raf = requestAnimationFrame(() => this.animate());
        return;
      }
      for (let i = 0; i < 3; i++) {
        const y = (this.h * (i + 1)) / 4 + Math.sin(this.gustTimer + i) * 30;
        const gradient = this.ctx.createLinearGradient(0, y, this.w, y);
        gradient.addColorStop(0, 'rgba(212, 165, 116, 0)');
        gradient.addColorStop(0.3, `rgba(212, 165, 116, ${0.015 + this.windGusts * 0.005})`);
        gradient.addColorStop(0.7, `rgba(212, 165, 116, ${0.01 + this.windGusts * 0.003})`);
        gradient.addColorStop(1, 'rgba(212, 165, 116, 0)');

        this.ctx.beginPath();
        this.ctx.moveTo(0, y - 1);
        this.ctx.lineTo(this.w, y - 1 + Math.sin(this.gustTimer * 2) * 5);
        this.ctx.lineTo(this.w, y + 1 + Math.sin(this.gustTimer * 2) * 5);
        this.ctx.lineTo(0, y + 1);
        this.ctx.fillStyle = gradient;
        this.ctx.fill();
      }

      this.raf = requestAnimationFrame(() => this.animate());
    }

    destroy() {
      cancelAnimationFrame(this.raf);
    }
  }

  /* ==========================================================
     2. SCROLL ANIMATIONS (Intersection Observer)
     ========================================================== */
  function initScrollAnimations() {
    const elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ==========================================================
     3. COUNTER ANIMATION
     ========================================================== */
  function initCounters() {
    const counters = document.querySelectorAll('[data-count]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  function animateCounter(el) {
    const target = parseInt(el.getAttribute('data-count'), 10);
    const suffix = el.getAttribute('data-suffix') || '';
    const duration = 1800;
    const start = performance.now();

    function step(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);

      el.textContent = current + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }

  /* ==========================================================
     4. SMOOTH SCROLL (for scroll indicator)
     ========================================================== */
  function initSmoothScroll() {
    const scrollBtn = document.querySelector('.toyota-hero__scroll');
    if (!scrollBtn) return;

    scrollBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector('.toyota-stats') || document.querySelector('.toyota-models');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ==========================================================
     5. HERO TITLE TRIGGER
     ========================================================== */
  function initHeroTitle() {
    const title = document.querySelector('.toyota-hero__title');
    if (!title) return;

    // Trigger the reveal shortly after page load
    setTimeout(() => {
      title.classList.add('is-visible');
    }, 300);
  }

  /* ==========================================================
     INIT — run when DOM is ready
     ========================================================== */
  function init() {
    // Desert dust particles
    const canvas = document.getElementById('toyotaParticles');
    if (canvas) {
      new DesertDust(canvas);
    }

    initHeroTitle();
    initScrollAnimations();
    initCounters();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
